// src/pages/News.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import imageCompression from "browser-image-compression";
import { db } from "../firebase";
import {
  FaEdit,
  FaEye,
  FaTrash,
  FaVideo,
  FaPlus,
  FaList,
  FaExclamationCircle,
  FaSearch,
  FaFilter,
} from "react-icons/fa";

/* Theme */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b91c1c",
  hold: "#7c3aed",
};

const predefinedTags = [
  "Bollywood Highlights",
  "Tollywood Highlights",
  "Kollywood Highlights",
  "Mollywood Highlights",
  "Sandalwood Highlights",
  "Pollywood Highlights",
  "Bhojpuri Cinema Highlights",
  "Marathi Cinema Highlights",
  "Gujarati Cinema Highlights",
  "Bengali Cinema Highlights",
  "Odia Cinema Highlights",
  "Music Industry Highlights",
  "DJ Industry Highlights",
  "Artist Highlights",
  "Pub | Club Highlights",
  "Trending TV Shows Highlights",
  "Red carpet Highlights",
  "BTS Highlights",
];

export default function News() {
  const [tab, setTab] = useState("add"); // "add" | "published"
  const [user, setUser] = useState(null);

  // list / table state
  const [newsList, setNewsList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  // create/edit form
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);

  // UI state
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ land: 0, port: 0, video: 0 });
  const [feedback, setFeedback] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);

  // list controls
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | active | inactive
  const [approvalFilter, setApprovalFilter] = useState("All"); // All | hold | approved

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("employee"));
    setUser(storedUser || null);
  }, []);

  const hasPermission =
    user?.role === "super_admin" || user?.permissions?.includes("publish_news");

  useEffect(() => {
    if (!hasPermission) return;
    setLoadingList(true);
    const unsub = onSnapshot(collection(db, "published_news"), (snapshot) => {
      const items = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(), // isFeatured comes through from Firestore if present
      }));
      setNewsList(items);
      setLoadingList(false);
    });
    return () => unsub();
  }, [hasPermission]);

  /* -------- Derived: filters + sorting -------- */
  const filteredSorted = useMemo(() => {
    let arr = [...newsList];

    if (q.trim()) {
      const s = q.trim().toLowerCase();
      arr = arr.filter((n) => {
        const hay = [
          n.news_id,
          n.title,
          n.description,
          n.location,
          n.author,
          n.article_type,
          (n.tags || []).join(" "),
          n.status,
          n.approval_status,
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(s);
      });
    }
    if (statusFilter !== "All") {
      arr = arr.filter((n) => String(n.status || "").toLowerCase() === statusFilter);
    }
    if (approvalFilter !== "All") {
      arr = arr.filter(
        (n) => String(n.approval_status || "").toLowerCase() === approvalFilter
      );
    }

    // newest first (based on published_at server timestamp if present)
    return arr.sort((a, b) => {
      const am = toMillis(a.published_at);
      const bm = toMillis(b.published_at);
      return bm - am;
    });
  }, [newsList, q, statusFilter, approvalFilter]);

  /* -------- Submit / Update -------- */
  async function handleSubmit() {
    if (!form.title.trim() || !form.description.trim()) {
      notify("❌ Title & description are required", "error");
      return;
    }

    setUploading(true);
    setFeedback(null);
    setProgress({ land: 0, port: 0, video: 0 });

    try {
      // Upload files (only when provided)
      const [landURL, portURL, videoURL] = await Promise.all([
        form.banner_landscape
          ? uploadWithCompression(form.banner_landscape, "Banner_Landscape", (p) =>
              setProgress((m) => ({ ...m, land: p }))
            )
          : editingId
          ? form.banner_landscape_url || ""
          : "",
        form.banner_portrait
          ? uploadWithCompression(form.banner_portrait, "Banner_Portrait", (p) =>
              setProgress((m) => ({ ...m, port: p }))
            )
          : editingId
          ? form.banner_portrait_url || ""
          : "",
        form.video
          ? uploadRaw(form.video, "Videos", (p) =>
              setProgress((m) => ({ ...m, video: p }))
            )
          : editingId
          ? form.video_url || ""
          : "",
      ]);

      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        tags: form.tags || [],
        location: form.location || "",
        status: form.status || "active",
        article_type: form.article_type || "",
        banner_landscape_url: landURL || "",
        banner_portrait_url: portURL || "",
        video_url: videoURL || "",
        author: user?.name || "Unknown",
        approval_status: editingId ? form.approval_status || "hold" : "hold",
        published_at: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, "published_news", editingId), payload);
        notify("✅ News updated successfully!", "success");
      } else {
        const newsId = generateNewsId();
        await addDoc(collection(db, "published_news"), {
          news_id: newsId,
          ...payload,
          news_likes: 0,
          comments: 0,
          views: 0,
          shares: 0,
        });
        notify("✅ News published successfully!", "success");
      }

      resetForm();
      setTab("published");
    } catch (err) {
      console.error(err);
      notify(`❌ Failed: ${err?.message || err}`, "error");
    } finally {
      setUploading(false);
      setProgress({ land: 0, port: 0, video: 0 });
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this news?")) return;
    try {
      await deleteDoc(doc(db, "published_news", id));
      notify("🗑️ News deleted successfully!", "success");
      if (selectedNews?.id === id) setSelectedNews(null);
      if (editingId === id) resetForm();
    } catch (err) {
      console.error(err);
      notify("❌ Failed to delete.", "error");
    }
  }

  async function handleStatusUpdate(id, status) {
    try {
      await updateDoc(doc(db, "published_news", id), {
        approval_status: status,
      });
      notify("✅ Status updated.", "success");
      if (editingId === id) {
        setForm((f) => ({ ...f, approval_status: status }));
      }
    } catch (err) {
      console.error(err);
      notify("❌ Failed to update status.", "error");
    }
  }

  // NEW: update isFeatured true/false
  async function handleFeaturedUpdate(id, isFeatured) {
    try {
      await updateDoc(doc(db, "published_news", id), {
        isFeatured,
      });
      notify("✅ Featured flag updated.", "success");
    } catch (err) {
      console.error(err);
      notify("❌ Failed to update featured flag.", "error");
    }
  }

  /* -------- Helpers -------- */
  function notify(message, type) {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 2500);
  }

  function resetForm() {
    setForm(emptyForm());
    setEditingId(null);
    setSelectedNews(null);
  }

  function startEdit(n) {
    setTab("add");
    setEditingId(n.id);
    setForm({
      title: n.title || "",
      description: n.description || "",
      tags: n.tags || [],
      location: n.location || "",
      status: n.status || "active",
      article_type: n.article_type || "",
      // keep URLs to reuse if not re-uploaded
      banner_landscape: null,
      banner_portrait: null,
      video: null,
      banner_landscape_url: n.banner_landscape_url || "",
      banner_portrait_url: n.banner_portrait_url || "",
      video_url: n.video_url || "",
      approval_status: n.approval_status || "hold",
    });
    setSelectedNews(null);
  }

  /* -------- UI: Layout -------- */
  if (user && !hasPermission) {
    return (
      <div style={styles.denied}>
        <FaExclamationCircle size={28} color="#e53935" />
        <h3>Access Denied</h3>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="aa-pro-page" style={styles.container}>
      <div className="aa-pro-hero" style={styles.hero}>
        <div style={styles.heroLeft}>
          <div className="aa-pro-hero-icon">
            <FaList />
          </div>
          <div>
            <h2 className="aa-pro-title">News Publisher</h2>
            <p className="aa-pro-subtitle">
              Create, review and manage published news with a sleek, pro UI.
            </p>
          </div>
        </div>
      </div>

      <div className="aa-pro-toolbar" style={styles.newsActions}>
        <button
          onClick={() => {
            setTab("add");
            if (!editingId) setSelectedNews(null);
          }}
          style={{ ...styles.actionBtn, ...(tab === "add" ? styles.actionBtnActive : {}) }}
        >
          <FaPlus /> {editingId ? "Edit News" : "Create News"}
        </button>
        <button
          onClick={() => setTab("published")}
          style={{
            ...styles.actionBtn,
            ...(tab === "published" ? styles.actionBtnActive : {}),
          }}
        >
          <FaList /> Manage News
        </button>
      </div>

      {/* Content */}
      {tab === "add" ? (
        <div>
          {renderForm({
            form,
            setForm,
            progress,
            uploading,
            onSubmit: handleSubmit,
            onCancel: editingId ? resetForm : null,
            isEditing: !!editingId,
          })}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <div
            className="aa-pro-toolbar"
            style={{
              ...styles.controlsRow,
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ display: "grid", gap: 2 }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: COLORS.text }}>
                Manage News
              </div>
              <div style={{ fontSize: 13, color: COLORS.subtext }}>
                Review, edit, approve and update all published news here.
              </div>
            </div>
            <button
              type="button"
              style={styles.ghostBtn}
              onClick={() => {
                setTab("add");
                setSelectedNews(null);
              }}
            >
              Back To Create
            </button>
          </div>
          {renderListControls({
            q,
            setQ,
            statusFilter,
            setStatusFilter,
            approvalFilter,
            setApprovalFilter,
          })}
          {renderTable({
            items: filteredSorted,
            loadingList,
            onView: setSelectedNews,
            onEdit: startEdit,
            onDelete: handleDelete,
            onStatusChange: handleStatusUpdate,
            onFeaturedChange: handleFeaturedUpdate, // NEW
          })}
        </div>
      )}

      {/* Drawer / Modal Preview */}
      {selectedNews &&
        renderPreview({ item: selectedNews, onClose: () => setSelectedNews(null) })}

      {/* Toast */}
      {feedback && (
        <div
          style={{
            ...styles.toast,
            background: feedback.type === "success" ? "#ecfdf5" : "#fef2f2",
            borderColor: feedback.type === "success" ? "#86efac" : "#fecaca",
            color: COLORS.text,
          }}
        >
          {feedback.message}
        </div>
      )}

      {/* Global overlay while uploading */}
      {uploading && (
        <div style={styles.overlay}>
          <div style={styles.loaderBox}>⏳ Uploading & Publishing…</div>
        </div>
      )}
    </div>
  );
}

/* ---------- Small UI blocks ---------- */
function renderForm({ form, setForm, progress, uploading, onSubmit, onCancel, isEditing }) {
  const disableSubmit = uploading || !form.title.trim() || !form.description.trim();

  return (
    <div className="aa-pro-table-shell" style={{ ...styles.card, borderRadius: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <div style={{ fontWeight: 900, color: COLORS.text, fontSize: 16 }}>
          {isEditing ? "Edit News" : "Create News"}
        </div>
      </div>

      <input
        style={styles.input}
        placeholder="Title"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
      />
      <textarea
        style={styles.textarea}
        placeholder="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
      />
      <input
        style={styles.input}
        placeholder="Location"
        value={form.location}
        onChange={(e) => setForm({ ...form, location: e.target.value })}
      />

      <div style={styles.label}>Quick Tags</div>
      <div style={styles.tagWrap}>
        {predefinedTags.map((tag) => {
          const active = form.tags.includes(tag);
          return (
            <button
              key={tag}
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  tags: active ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag],
                }))
              }
              style={{ ...styles.tagBtn, ...(active ? styles.tagBtnActive : {}) }}
              type="button"
            >
              #{tag}
            </button>
          );
        })}
      </div>

      <div style={styles.gridThree}>
        <div>
          <div style={styles.label}>Banner (Landscape)</div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              setForm({ ...form, banner_landscape: e.target.files?.[0] || null })
            }
          />
          {progress.land > 0 && <ProgressBar value={progress.land} />}
        </div>
        <div>
          <div style={styles.label}>Banner (Portrait)</div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              setForm({ ...form, banner_portrait: e.target.files?.[0] || null })
            }
          />
          {progress.port > 0 && <ProgressBar value={progress.port} />}
        </div>
        <div>
          <div style={styles.label}>Video (optional)</div>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setForm({ ...form, video: e.target.files?.[0] || null })}
          />
          {progress.video > 0 && <ProgressBar value={progress.video} />}
        </div>
      </div>

      <div className="row" style={styles.gridTwo}>
        <div>
          <div style={styles.label}>Status</div>
          <select
            style={styles.select}
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <div style={styles.label}>Article Type</div>
          <select
            style={styles.select}
            value={form.article_type}
            onChange={(e) => setForm({ ...form, article_type: e.target.value })}
          >
            <option value="">— Select (optional) —</option>
            <option value="Trending Topics">Trending Topics</option>
            <option value="Hottest Article">Hottest Article</option>
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          style={{ ...styles.primaryBtn, opacity: disableSubmit ? 0.7 : 1 }}
          disabled={disableSubmit}
          onClick={onSubmit}
        >
          {isEditing ? "Update News" : "Publish News"}
        </button>
        {onCancel && (
          <button style={styles.ghostBtn} onClick={onCancel} disabled={uploading}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function renderListControls({
  q,
  setQ,
  statusFilter,
  setStatusFilter,
  approvalFilter,
  setApprovalFilter,
}) {
  return (
    <div className="aa-pro-toolbar" style={styles.controlsRow}>
      <div style={styles.searchBox}>
        <FaSearch style={{ opacity: 0.7 }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title, description, tags, author, id…"
          style={styles.searchInput}
        />
      </div>
      <div style={styles.controlGroup}>
        <FaFilter />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.selectBare}
        >
          <option>All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div style={styles.controlGroup}>
        <FaFilter />
        <select
          value={approvalFilter}
          onChange={(e) => setApprovalFilter(e.target.value)}
          style={styles.selectBare}
        >
          <option>All</option>
          <option value="hold">Hold</option>
          <option value="approved">Approved</option>
        </select>
      </div>
    </div>
  );
}

function renderTable({
  items,
  loadingList,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onFeaturedChange,
}) {
  return (
    <div className="aa-pro-table-shell" style={styles.tableWrap}>
      {loadingList ? (
        <div style={{ padding: 14, color: COLORS.subtext }}>Loading…</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <Th sticky>News ID</Th>
                <Th sticky>Landscape</Th>
                <Th sticky>Portrait</Th>
                <Th sticky>Video</Th>
                <Th sticky>Title</Th>
                <Th sticky>Status</Th>
                <Th sticky>Approval</Th>
                <Th sticky>Featured</Th>{/* NEW */}
                <Th sticky>Author</Th>
                <Th sticky>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: 12, color: COLORS.subtext }}>
                    No items found.
                  </td>
                </tr>
              ) : (
                items.map((n, i) => {
                  const rowBg = i % 2 ? "#fafafa" : "#fff";
                  return (
                    <tr
                      key={n.id}
                      style={{
                        borderTop: `1px solid ${COLORS.border}`,
                        background: rowBg,
                        transition: "background .15s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = rowBg)}
                    >
                      <Td mono title={n.news_id || n.id}>
                        {(n.news_id || n.id || "—").slice(0, 8)}
                        {(n.news_id || n.id || "—").length > 8 ? "…" : ""}
                      </Td>
                      <Td>
                        {n.banner_landscape_url ? (
                          <img
                            src={n.banner_landscape_url}
                            alt="landscape"
                            style={{
                              height: 40,
                              borderRadius: 6,
                              border: `1px solid ${COLORS.border}`,
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td>
                        {n.banner_portrait_url ? (
                          <img
                            src={n.banner_portrait_url}
                            alt="portrait"
                            style={{
                              height: 40,
                              borderRadius: 6,
                              border: `1px solid ${COLORS.border}`,
                            }}
                          />
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td>{n.video_url ? <FaVideo title="Has video" /> : "—"}</Td>
                      <Td>{n.title || "—"}</Td>
                      <Td>
                        <Chip tone={n.status === "active" ? "ok" : "warn"}>
                          {n.status || "—"}
                        </Chip>
                      </Td>
                      <Td>
                        <select
                          value={n.approval_status || "hold"}
                          onChange={(e) => onStatusChange(n.id, e.target.value)}
                          style={styles.selectSmall}
                        >
                          <option value="hold">Hold</option>
                          <option value="approved">Approved</option>
                        </select>
                      </Td>
                      <Td>
                        <select
                          value={n.isFeatured ? "true" : "false"}
                          onChange={(e) =>
                            onFeaturedChange(n.id, e.target.value === "true")
                          }
                          style={styles.selectSmall}
                        >
                          <option value="false">False</option>
                          <option value="true">True</option>
                        </select>
                      </Td>
                      <Td>{n.author || "—"}</Td>
                      <Td>
                        <div style={{ display: "flex", gap: 8 }}>
                          <IconBtn title="Preview" onClick={() => onView(n)}>
                            <FaEye />
                          </IconBtn>
                          <IconBtn title="Edit" onClick={() => onEdit(n)}>
                            <FaEdit />
                          </IconBtn>
                          <IconBtn title="Delete" danger onClick={() => onDelete(n.id)}>
                            <FaTrash />
                          </IconBtn>
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function renderPreview({ item, onClose }) {
  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalCard}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontWeight: 900, color: COLORS.text }}>News Details</div>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
            ✕
          </button>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <Row label="News ID" value={item.news_id || item.id} mono />
          <Row label="Title" value={item.title} />
          <Row label="Description" value={item.description} />
          <Row label="Location" value={item.location || "—"} />
          <Row label="Status" value={item.status || "—"} />
          <Row label="Approval" value={item.approval_status || "—"} />
          <Row label="Article Type" value={item.article_type || "—"} />
          <Row label="Author" value={item.author || "—"} />
          <Row label="Published At" value={formatDateTime(item.published_at) || "—"} />

          <div style={{ display: "grid", gap: 10 }}>
            {item.banner_landscape_url && (
              <img
                src={item.banner_landscape_url}
                alt="landscape"
                style={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "cover",
                  borderRadius: 10,
                  border: `1px solid ${COLORS.border}`,
                }}
              />
            )}
            {item.banner_portrait_url && (
              <img
                src={item.banner_portrait_url}
                alt="portrait"
                style={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "contain",
                  borderRadius: 10,
                  border: `1px solid ${COLORS.border}`,
                }}
              />
            )}
            {item.video_url && (
              <video
                src={item.video_url}
                controls
                style={{ width: "100%", borderRadius: 10, border: `1px solid ${COLORS.border}` }}
              />
            )}
          </div>

          {item.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {item.tags.map((t) => (
                <span key={t} style={styles.pill}>
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Reusable atoms ---------- */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, mono, title }) {
  return (
    <td
      title={title}
      style={{
        padding: 10,
        color: COLORS.text,
        verticalAlign: "top",
        fontFamily: mono
          ? "ui-monospace, SFMono-Regular, Menlo, monospace"
          : "inherit",
      }}
    >
      {children}
    </td>
  );
}
function Row({ label, value, mono }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10 }}>
      <div style={{ color: COLORS.subtext, fontWeight: 700 }}>{label}</div>
      <div style={{ color: COLORS.text, fontFamily: mono ? "monospace" : "inherit" }}>
        {value}
      </div>
    </div>
  );
}
function Chip({ tone = "ok", children }) {
  const map = {
    ok: { bg: "#ecfdf5", fg: COLORS.ok },
    warn: { bg: "#fef2f2", fg: COLORS.warn },
  };
  const v = map[tone] || map.ok;
  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: v.bg,
        color: v.fg,
        fontWeight: 800,
        border: `1px solid ${v.fg}33`,
      }}
    >
      {children}
    </span>
  );
}
function IconBtn({ children, onClick, title, danger }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        border: `1px solid ${danger ? "#fecaca" : COLORS.border}`,
        background: "#fff",
        color: danger ? COLORS.warn : COLORS.text,
        width: 34,
        height: 34,
        borderRadius: 8,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
function ProgressBar({ value }) {
  return (
    <div
      style={{
        marginTop: 6,
        height: 8,
        background: "#f1f5f9",
        borderRadius: 999,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${Math.max(0, Math.min(100, value))}%`,
          height: "100%",
          background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
        }}
      />
    </div>
  );
}

/* ---------- Pure helpers ---------- */
function emptyForm() {
  return {
    title: "",
    description: "",
    tags: [],
    location: "",
    status: "active",
    article_type: "",
    banner_landscape: null,
    banner_portrait: null,
    video: null,
    // when editing, we keep these URLs to reuse if no new file is selected
    banner_landscape_url: "",
    banner_portrait_url: "",
    video_url: "",
    approval_status: "hold",
  };
}
function generateNewsId() {
  const randomStr = Math.random().toString(36).substring(2, 8);
  const randomNum = Math.floor(Math.random() * 900000 + 100000);
  return `news${randomNum}${randomStr}`;
}
function toMillis(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts === "number") return ts;
    if (ts?.toDate) return ts.toDate().getTime();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}
async function uploadWithCompression(file, folder, onProgress) {
  const storage = getStorage();
  const name = `${Date.now()}-${file.name}`;
  const path = `published_news/${folder}/${name}`;
  const fileRef = ref(storage, path);

  const compressed = await imageCompression(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  });

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, compressed);
    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        onProgress?.(pct);
      },
      (err) => reject(err),
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}
async function uploadRaw(file, folder, onProgress) {
  const storage = getStorage();
  const name = `${Date.now()}-${file.name}`;
  const path = `published_news/${folder}/${name}`;
  const fileRef = ref(storage, path);

  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, file);
    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        onProgress?.(pct);
      },
      (err) => reject(err),
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}

function formatDateTime(v) {
  try {
    if (!v) return "";
    const d =
      typeof v === "string"
        ? new Date(v)
        : v?.toDate
        ? v.toDate()
        : typeof v === "number"
        ? new Date(v)
        : new Date(v);
    if (isNaN(d.getTime())) return "";
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch {
    return "";
  }
}

/* ---------- Styles ---------- */
const styles = {
  container: {
    padding: 16,
    fontFamily: "Inter, system-ui, sans-serif",
    backgroundColor: COLORS.page,
    minHeight: "100vh",
    display: "grid",
    gap: 12,
  },
  hero: {
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    color: "#fff",
    padding: "14px 16px",
    borderRadius: 14,
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 14px 26px rgba(0,0,0,0.12)",
  },
  heroLeft: { display: "flex", alignItems: "center", gap: 12 },
  heroIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.35)",
  },
  heroTitle: { margin: 0, fontSize: 20, fontWeight: 900, lineHeight: 1.1 },
  heroSub: { margin: "4px 0 0", opacity: 0.92, fontSize: 13 },
  tabs: { display: "flex", gap: 8, flexWrap: "wrap" },
  newsActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
    padding: "14px 16px",
    borderRadius: 16,
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    boxShadow: "0 16px 32px rgba(15, 23, 42, 0.06)",
  },
  actionBtn: {
    minHeight: 46,
    padding: "0 18px",
    borderRadius: 14,
    border: `1px solid ${COLORS.border}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
    background: "#fff",
    color: COLORS.text,
    fontWeight: 900,
    fontSize: 14,
  },
  actionBtnActive: {
    color: "#fff",
    border: "1px solid transparent",
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    boxShadow: "0 16px 28px rgba(242, 12, 83, 0.22)",
  },
  tabBtn: {
    border: `1px solid #ffffff66`,
    borderRadius: 999,
    padding: "8px 12px",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    fontWeight: 800,
  },
  tabActive: {
    background: "#fff",
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
  },

  gridTwo: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  gridThree: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },

  card: {
    background: COLORS.card,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 12,
    boxShadow: "0 6px 14px rgba(0,0,0,.06)",
  },

  input: {
    width: "100%",
    padding: "10px",
    marginBottom: "0.75rem",
    borderRadius: "8px",
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    fontSize: 14,
  },
  textarea: {
    width: "100%",
    padding: "10px",
    height: 110,
    marginBottom: "0.75rem",
    borderRadius: "8px",
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    fontSize: 14,
    resize: "vertical",
  },
  label: { fontWeight: 800, color: COLORS.text, margin: "4px 0" },

  tagWrap: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 },
  tagBtn: {
    padding: "6px 10px",
    background: "#f1f5f9",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 12,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.text,
    fontWeight: 700,
  },
  tagBtnActive: {
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    color: "#fff",
    border: "none",
  },

  pill: {
    padding: "4px 8px",
    background: "#f1f5f9",
    borderRadius: 999,
    border: `1px solid ${COLORS.border}`,
    fontSize: 12,
    fontWeight: 800,
    color: COLORS.text,
  },

  select: {
    padding: "10px",
    width: "100%",
    borderRadius: "8px",
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    fontSize: 14,
  },
  primaryBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 10,
    border: "none",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  ghostBtn: {
    height: 38,
    padding: "0 14px",
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    color: COLORS.text,
    cursor: "pointer",
    fontWeight: 800,
  },

  controlsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    background: "#fff",
    minWidth: 260,
    flex: 1,
  },
  searchInput: {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: 14,
  },
  controlGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "8px 10px",
    background: "#fff",
  },
  selectBare: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 14,
    paddingRight: 4,
    cursor: "pointer",
  },

  tableWrap: {
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 6px 14px rgba(0,0,0,.06)",
    border: `1px solid ${COLORS.border}`,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 980,
    fontSize: 14,
  },
  selectSmall: {
    padding: "6px",
    fontSize: "0.9rem",
    borderRadius: "8px",
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    display: "grid",
    placeItems: "center",
    zIndex: 60,
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 780,
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: 14,
    boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  },
  closeBtn: {
    border: `1px solid ${COLORS.border}`,
    background: "#fff",
    color: COLORS.text,
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
  },

  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  loaderBox: {
    background: "#fff",
    padding: "1.2rem 1.4rem",
    borderRadius: "10px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: "1rem",
    border: `1px solid ${COLORS.border}`,
  },

  toast: {
    position: "fixed",
    bottom: 16,
    right: 16,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 10,
    padding: "10px 12px",
    boxShadow: "0 8px 24px rgba(0,0,0,.12)",
    fontWeight: 800,
    zIndex: 80,
  },

  denied: {
    backdropFilter: "blur(5px)",
    backgroundColor: "rgba(0,0,0,0.3)",
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    color: "#fff",
    fontFamily: "Inter, system-ui, sans-serif",
    zIndex: 999,
    gap: 6,
  },
};
